import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FolderService } from '../api/FolderService';
import { Foldertype } from '../types/FolderTypes';
import { Folder } from '../api/protosCompiled/folder/folder_pb';

//-------------------Project Folders------------------------
export function useFetchProjectFolders() {
  const foldertype: Foldertype = 'project';
  const { data, error, isLoading } = useQuery(
    ['projectFolders'],
    async () => {
      const response = await FolderService.getFolders({
        foldertype: foldertype,
      });
      return response;
    },
    {
      enabled: true,
    }
  );

  return { data, error, isLoading };
}

export function useCreateProjectFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (updatedFolder: Folder.AsObject) => {
      await FolderService.createFolder(updatedFolder);
    },
    {
      onMutate: async (updatedFolder) => {
        // Optimistically update project folders cache
        await queryClient.cancelQueries(['projectFolders']);
        const previousProjectFolders = queryClient.getQueryData([
          'projectFolders',
        ]);

        queryClient.setQueryData(['projectFolders'], (old: any) =>
          old?.foldersList?.map((folder: Folder.AsObject) =>
            folder.folderid === updatedFolder.folderid
              ? { ...folder, ...updatedFolder }
              : folder
          )
        );

        return { previousProjectFolders };
      },
      onError: (err, updatedFolder, context) => {
        // Roll back project folders cache on error
        if (context?.previousProjectFolders) {
          queryClient.setQueryData(
            ['projectFolders'],
            context.previousProjectFolders
          );
        }
      },
      onSettled: () => {
        // Invalidate the project folders query to refetch data
        queryClient.invalidateQueries(['projectFolders']);
      },
    }
  );
}

export function useUpdateProjectFolder() {
  const queryClient = useQueryClient();

  return useMutation(
    async (updatedFolder: Folder.AsObject) => {
      await FolderService.updateFolder(updatedFolder);
    },
    {
      onMutate: async (updatedFolder) => {
        // Optimistically update project folders cache
        await queryClient.cancelQueries(['projectFolders']);
        const previousProjectFolders = queryClient.getQueryData([
          'projectFolders',
        ]);

        queryClient.setQueryData(['projectFolders'], (old: any) =>
          old.foldersList.map((folder: Folder.AsObject) =>
            folder.folderid === updatedFolder.folderid
              ? { ...folder, ...updatedFolder }
              : folder
          )
        );

        return { previousProjectFolders };
      },
      onError: (err, updatedFolder, context) => {
        // Roll back project folders cache on error
        if (context?.previousProjectFolders) {
          queryClient.setQueryData(
            ['projectFolders'],
            context.previousProjectFolders
          );
        }
      },
      onSettled: () => {
        // Invalidate the project folders query to refetch data
        queryClient.invalidateQueries(['projectFolders']);
      },
    }
  );
}

export function useDeleteProjectFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (folderId: string) => {
      await FolderService.deleteFolder(folderId);
    },
    {
      onMutate: async (folderId) => {
        // Optimistically update project folders cache
        await queryClient.cancelQueries(['projectFolders']);
        const previousProjectFolders = queryClient.getQueryData([
          'projectFolders',
        ]);

        queryClient.setQueryData(['projectFolders'], (old: any) =>
          old.foldersList.filter(
            (folder: Folder.AsObject) => folder.folderid !== folderId
          )
        );

        return { previousProjectFolders };
      },
      onError: (err, folderId, context) => {
        // Roll back project folders cache on error
        if (context?.previousProjectFolders) {
          queryClient.setQueryData(
            ['projectFolders'],
            context.previousProjectFolders
          );
        }
      },
      onSettled: () => {
        // Invalidate the project folders query to refetch data
        queryClient.invalidateQueries(['projectFolders']);
      },
    }
  );
}

//-------------------Assembly Folders------------------------
export function useFetchAssemblyFolders() {
  const foldertype: Foldertype = 'assembly';
  const { data, error, isLoading } = useQuery(
    ['assemblyFolders'],
    async () => {
      const response = await FolderService.getFolders({
        foldertype: foldertype,
      });
      return response;
    },
    {
      enabled: true,
    }
  );
  return { data, error, isLoading };
}

// For assembly folders
export function useCreateAssemblyFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (newFolder: Folder.AsObject) => {
      const response = await FolderService.createFolder(newFolder);
      return response.folder;
    },
    {
      onMutate: async (newFolder) => {
        await queryClient.cancelQueries(['assemblyFolders']);
        const previousFolders = queryClient.getQueryData(['assemblyFolders']);

        const tempFolder = {
          ...newFolder,
          folderid: `temp-${Date.now()}`,
        };

        queryClient.setQueryData(['assemblyFolders'], (old: any) => ({
          ...old,
          foldersList: [...(old?.foldersList || []), tempFolder],
        }));

        return { previousFolders };
      },
      onError: (err, newFolder, context) => {
        if (context?.previousFolders) {
          queryClient.setQueryData(
            ['assemblyFolders'],
            context.previousFolders
          );
        }
      },
      onSuccess: (serverFolder, newFolder) => {
        queryClient.setQueryData(['assemblyFolders'], (old: any) => ({
          ...old,
          foldersList: old.foldersList.map((folder: Folder.AsObject) => {
            if (
              folder.name === newFolder.name &&
              folder.folderid.startsWith('temp-')
            ) {
              return serverFolder;
            }
            return folder;
          }),
        }));
      },
    }
  );
}

export function useUpdateAssemblyFolder() {
  const queryClient = useQueryClient();

  return useMutation(
    async (updatedFolder: Folder.AsObject) => {
      const response = await FolderService.updateFolder(updatedFolder);
      return response.folder;
    },
    {
      onMutate: async (updatedFolder) => {
        await queryClient.cancelQueries(['assemblyFolders']);
        const previousFolders = queryClient.getQueryData(['assemblyFolders']);

        queryClient.setQueryData(['assemblyFolders'], (old: any) => ({
          ...old,
          foldersList: old.foldersList.map((folder: Folder.AsObject) =>
            folder.folderid === updatedFolder.folderid ? updatedFolder : folder
          ),
        }));

        return { previousFolders };
      },
      onError: (err, updatedFolder, context) => {
        if (context?.previousFolders) {
          queryClient.setQueryData(
            ['assemblyFolders'],
            context.previousFolders
          );
        }
      },
      onSuccess: (serverFolder, updatedFolder) => {
        queryClient.setQueryData(['assemblyFolders'], (old: any) => ({
          ...old,
          foldersList: old.foldersList.map((folder: Folder.AsObject) =>
            folder.folderid === updatedFolder.folderid ? serverFolder : folder
          ),
        }));
      },
    }
  );
}

export function useDeleteAssemblyFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (folderId: string) => {
      const response = await FolderService.deleteFolder(folderId);
      return response;
    },
    {
      onMutate: async (folderId) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['assemblyFolders']);

        // Snapshot the previous value
        const previousFolders = queryClient.getQueryData(['assemblyFolders']);

        // Optimistically remove the folder from the list
        queryClient.setQueryData(['assemblyFolders'], (old: any) => ({
          ...old,
          foldersList: old.foldersList.filter(
            (folder: Folder.AsObject) => folder.folderid !== folderId
          ),
        }));

        return { previousFolders };
      },
      onError: (err, folderId, context) => {
        // If there was an error, roll back
        if (context?.previousFolders) {
          queryClient.setQueryData(
            ['assemblyFolders'],
            context.previousFolders
          );
        }
      },
      // Remove the onSettled handler to prevent unnecessary refetches
      onSuccess: () => {
        // We don't need to do anything here since our optimistic update
        // already removed the folder from the list
      },
    }
  );
}

//-------------------Item Folders------------------------
export function useFetchItemFolders() {
  const foldertype: Foldertype = 'item';

  const { data, error, isLoading } = useQuery(
    ['itemFolders'],
    async () => {
      const response = await FolderService.getFolders({
        foldertype: foldertype,
      });
      return response;
    },
    {
      enabled: true,
    }
  );

  return { data, error, isLoading };
}

export function useCreateItemFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (newFolder: Folder.AsObject) => {
      const response = await FolderService.createFolder(newFolder);
      return response.folder;
    },
    {
      onMutate: async (newFolder) => {
        await queryClient.cancelQueries(['itemFolders']);
        const previousFolders = queryClient.getQueryData(['itemFolders']);

        const tempFolder = {
          ...newFolder,
          folderid: `temp-${Date.now()}`,
        };

        queryClient.setQueryData(['itemFolders'], (old: any) => ({
          ...old,
          foldersList: [...(old?.foldersList || []), tempFolder],
        }));

        return { previousFolders };
      },
      onError: (err, newFolder, context) => {
        if (context?.previousFolders) {
          queryClient.setQueryData(['itemFolders'], context.previousFolders);
        }
      },
      onSuccess: (serverFolder, newFolder) => {
        queryClient.setQueryData(['itemFolders'], (old: any) => ({
          ...old,
          foldersList: old.foldersList.map((folder: Folder.AsObject) => {
            if (
              folder.name === newFolder.name &&
              folder.folderid.startsWith('temp-')
            ) {
              return serverFolder;
            }
            return folder;
          }),
        }));
      },
    }
  );
}

export function useUpdateItemFolder() {
  const queryClient = useQueryClient();

  return useMutation(
    async (updatedFolder: Folder.AsObject) => {
      const response = await FolderService.updateFolder(updatedFolder);
      return response.folder;
    },
    {
      onMutate: async (updatedFolder) => {
        await queryClient.cancelQueries(['itemFolders']);
        const previousFolders = queryClient.getQueryData(['itemFolders']);

        queryClient.setQueryData(['itemFolders'], (old: any) => ({
          ...old,
          foldersList: old.foldersList.map((folder: Folder.AsObject) =>
            folder.folderid === updatedFolder.folderid ? updatedFolder : folder
          ),
        }));

        return { previousFolders };
      },
      onError: (err, updatedFolder, context) => {
        if (context?.previousFolders) {
          queryClient.setQueryData(['itemFolders'], context.previousFolders);
        }
      },
      onSuccess: (serverFolder, updatedFolder) => {
        queryClient.setQueryData(['itemFolders'], (old: any) => ({
          ...old,
          foldersList: old.foldersList.map((folder: Folder.AsObject) =>
            folder.folderid === updatedFolder.folderid ? serverFolder : folder
          ),
        }));
      },
    }
  );
}

export function useDeleteItemFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (folderId: string) => {
      const response = await FolderService.deleteFolder(folderId);
      return response;
    },
    {
      onMutate: async (folderId) => {
        await queryClient.cancelQueries(['itemFolders']);
        const previousFolders = queryClient.getQueryData(['itemFolders']);

        queryClient.setQueryData(['itemFolders'], (old: any) => ({
          ...old,
          foldersList: old.foldersList.filter(
            (folder: Folder.AsObject) => folder.folderid !== folderId
          ),
        }));

        return { previousFolders };
      },
      onError: (err, folderId, context) => {
        if (context?.previousFolders) {
          queryClient.setQueryData(['itemFolders'], context.previousFolders);
        }
      },
      onSuccess: () => {
        // We don't need to do anything here since our optimistic update
        // already removed the folder from the list
      },
    }
  );
}
